import React, { useState, useEffect } from 'react';

// Services
import { getInventario } from '../../../services/StoreService';
import { registrarVentas } from '../../../services/VentasService';

import { buscarDni } from '../../../services/DniService'; 
import { guardarCliente, getClientes } from '../../../services/ClientesService'; 

import { generarPDFVenta, generarTicketVenta } from '../../../services/PdfVentasService';

// Estilos
import '../../static/Global.css';
import '../../static/Home.css';
import '../../static/Aside.css';
import '../../static/Flotante.css';
import '../../static/RegistrarMovimiento.css';

const RegistrarVentas = ({ usuarioLogueado = "Usuario Novatech" }) => {
    const [vendedor, setVendedor] = useState({ id: null, nombre: "Cargando..." });
    const [busqueda, setBusqueda] = useState('');
    const [tipoComprobante, setTipoComprobante] = useState('ticket');
    const [productosDB, setProductosDB] = useState([]);
    const [carrito, setCarrito] = useState([]);
    const [total, setTotal] = useState(0);

    const [clienteSeleccionado, setClienteSeleccionado] = useState('DESCONOCIDO');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loadingDni, setLoadingDni] = useState(false);

    const [formData, setFormData] = useState({
        id: '',
        dni: '',
        nombres: '',
        apellido_paterno: '',
        apellido_materno: '',
        email: '',
        num_cel: '', 
        comments: '' 
    });

    // CONSULTAR DNI (API EXTERNA RENIEC)
    const handleConsultarDni = async () => {
        if (formData.dni.length !== 8) {
            alert("El DNI debe tener exactamente 8 dígitos.");
            return;
        }

        setLoadingDni(true);
        try {
            const data = await buscarDni(formData.dni);
            
            if (data.success) {
                setFormData(prev => ({
                    ...prev,
                    nombres: data.nombres,
                    apellido_paterno: data.apellido_paterno,
                    apellido_materno: data.apellido_materno
                }));
            } else {
                alert("No se encontró el DNI en RENIEC o el servicio no está disponible. Ingrésalo manualmente.");
            }
        } catch (error) {
            alert("Error al conectar con RENIEC: " + error.message);
        } finally {
            setLoadingDni(false);
        }
    };

    const openModal = () => {
        setFormData({
            id: '',
            dni: '',
            nombres: '',
            apellido_paterno: '',
            apellido_materno: '',
            email: '',
            num_cel: '', 
            comments: ''
        });
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    // CREAR CLIENTE NUEVO O VERIFICAR LOCALMENTE
    const handleSave = async (e) => {
        e.preventDefault();
        
        if (formData.dni.length !== 8) {
            alert("El DNI debe tener exactamente 8 dígitos.");
            return;
        }

        try {
            // 1. SOLUCIÓN: Validamos de manera local. 
            // Si tienes un endpoint como obtenerClientePorDni(formData.dni), úsalo aquí.
            // Si no, podemos listar temporalmente y buscar la coincidencia:
            const listaClientesLocales = await getClientes(); 
            const clienteExistente = listaClientesLocales.find(c => c.dni === formData.dni);
            
            if (clienteExistente) {
                // Si existe en TU BASE DE DATOS, lo asignamos de inmediato
                const nombreCompleto = `${clienteExistente.nombres} ${clienteExistente.apellidos || ''}`.trim().toUpperCase();
                
                setClienteSeleccionado(nombreCompleto);
                alert(`ℹ️ El cliente con DNI ${formData.dni} ya está registrado en el sistema. Se ha asignado a la venta actual.`);
                closeModal();
                return; 
            }

            // 2. Si no existe en tu sistema local, se procede con el registro real en PostgreSQL
            await guardarCliente(formData);
            
            const nombreCompletoNuevo = `${formData.nombres} ${formData.apellido_paterno} ${formData.apellido_materno}`.trim().toUpperCase();
            
            setClienteSeleccionado(nombreCompletoNuevo);
            alert(`✅ ¡Cliente "${nombreCompletoNuevo}" registrado y asignado a la venta!`);
            closeModal();

        } catch (error) {
            alert("Error al procesar el cliente: " + error.message);
        }
    };

    useEffect(() => {
        const userSession = sessionStorage.getItem('usuario_novatech') || localStorage.getItem('usuario_novatech');
        if (userSession) {
            try {
                const user = JSON.parse(userSession);
                setVendedor({
                    id: user.id,
                    nombre: user.nombre
                });
            } catch (e) {
                console.error("Error al recuperar el nombre:", e);
                setVendedor({ id: null, nombre: "Error" });
            }
        } else {
            setVendedor({ id: null, nombre: "Invitado" });
        }
    }, []);

    // Carga inicial de productos
    useEffect(() => {
        const fetchProds = async () => {
            try {
                const data = await getInventario();
                setProductosDB(data);
            } catch (error) {
                console.error("Error cargando inventario:", error);
            }
        };
        fetchProds();
    }, []);

    // Recalcular total automáticamente
    useEffect(() => {
        const nuevoTotal = carrito.reduce((acc, item) => acc + item.subtotal, 0);
        setTotal(nuevoTotal);
    }, [carrito]);

    const agregarAlCarrito = (prod) => {
        const existe = carrito.find(item => item.articulo_id === prod.productos_id);
        if (existe) {
            actualizarCantidad(prod.productos_id, existe.cantidad + 1);
        } else {
            const nuevoItem = {
                articulo_id: prod.productos_id,
                nombre: prod.nombre,
                precio_venta: parseFloat(prod.precio_venta),
                cantidad: 1,
                subtotal: parseFloat(prod.precio_venta)
            };
            setCarrito([...carrito, nuevoItem]);
        }
        setBusqueda('');
    };

    const actualizarCantidad = (id, nuevaCant) => {
        if (nuevaCant < 1) return;
        setCarrito(prevCarrito => 
            prevCarrito.map(item => {
                if (item.articulo_id === id) {
                    const cantCalculada = Number(nuevaCant);
                    return { 
                        ...item, 
                        cantidad: cantCalculada, 
                        subtotal: cantCalculada * item.precio_venta 
                    };
                }
                return item;
            })
        );
    };

    const quitarProducto = (id) => {
        setCarrito(carrito.filter(item => item.articulo_id !== id));
    };

    const cancelarVenta = () => {
        if (window.confirm("¿Estás seguro de cancelar la venta actual?")) {
            setCarrito([]);
            setBusqueda('');
            setClienteSeleccionado('DESCONOCIDO'); 
        }
    };

    const procesarVentas = async () => {
        if (carrito.length === 0) return alert("El carrito está vacío");

        const ventaPayload = {
            usuario: vendedor.nombre,
            cliente: clienteSeleccionado, 
            total: total,
            detalles: carrito.map(item => ({
                articulo_id: item.articulo_id,
                nombre: item.nombre,
                cantidad: item.cantidad,
                precio_venta: item.precio_venta,
                subtotal: item.subtotal
            }))
        };

        try {
            const data = await registrarVentas(ventaPayload);

            if (data.success) {
                alert(`✅ Venta #${data.venta_id} registrada con éxito.`);
                const datosParaPDF = {
                    venta_id: data.venta_id,
                    usuario: vendedor.nombre,
                    cliente: clienteSeleccionado, 
                    detalles: ventaPayload.detalles,
                    total: total
                };

                if (tipoComprobante === 'ticket') {
                    generarTicketVenta(datosParaPDF);
                } else {
                    generarPDFVenta(datosParaPDF);
                }

                setCarrito([]); 
                setBusqueda(''); 
                setClienteSeleccionado('DESCONOCIDO'); 
            }
        } catch (err) {
            alert(`❌ Error: ${err.message}`);
        }
    };

    const productosFiltrados = busqueda.length > 0 
        ? productosDB.filter(p => p.nombre.toLowerCase().includes(busqueda.toLowerCase()))
        : [];

    return (
        <div className="venta-container">
            <header className="venta-header">
                <h2>Registrar Venta</h2>
                <div className="user-info">
                    Vendedor: <i className="fa fa-user-circle" style={{ marginRight: '5px' }}></i> 
                    <strong style={{ color: '#2c3e50' }}>{vendedor.nombre}</strong>
                    {vendedor.rol && <span className="badge-rol">{vendedor.rol}</span>}
                </div>
            </header>

            <div className="venta-layout">
                <div className="venta-main">
                    <div className="search-box">
                        <input 
                            type="text" 
                            placeholder="Buscar producto (Ej: Teclado, Mouse...)" 
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            autoFocus
                        />
                        {productosFiltrados.length > 0 && (
                            <ul className="search-results">
                                {productosFiltrados.map(p => (
                                    <li key={p.productos_id} onClick={() => agregarAlCarrito(p)}>
                                        <div className="res-info">
                                            <strong>{p.nombre}</strong>
                                            <span>S/ {p.precio_venta}</span>
                                        </div>
                                        <span className="res-stock">Stock: {p.stock}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <table className="novatech-table">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th>Precio</th>
                                <th style={{ width: '120px' }}>Cantidad</th>
                                <th>Subtotal</th>
                                <th>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carrito.length === 0 ? (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                        No hay productos en la venta. Usa el buscador arriba.
                                    </td>
                                </tr>
                            ) : (
                                carrito.map(item => (
                                    <tr key={item.articulo_id}>
                                        <td>{item.nombre}</td>
                                        <td>S/ {item.precio_venta.toFixed(2)}</td>
                                        <td>
                                            <div className="qty-controls">
                                                <button onClick={() => actualizarCantidad(item.articulo_id, item.cantidad - 1)}>-</button>
                                                <span>{item.cantidad}</span>
                                                <button onClick={() => actualizarCantidad(item.articulo_id, item.cantidad + 1)}>+</button>
                                            </div>
                                        </td>
                                        <td>S/ {item.subtotal.toFixed(2)}</td>
                                        <td>
                                            <button className="btn-remove" onClick={() => quitarProducto(item.articulo_id)}>
                                                <i className="fa fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <aside className="venta-summary">
                    <div className="form-group" style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px', border: '1px dashed #cbd5e1' }}>
                        <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569', display: 'block', marginBottom: '5px', textTransform: 'uppercase' }}>
                            Cliente Asignado
                        </label>
                        <input 
                            type="text"
                            className="form-control"
                            value={clienteSeleccionado}
                            onChange={(e) => setClienteSeleccionado(e.target.value.toUpperCase())}
                            style={{ width: '100%', padding: '6px 10px', fontSize: '13px', borderRadius: '4px', border: '1px solid #cbd5e1', fontWeight: '600', color: '#1e293b', background: '#fff' }}
                            placeholder="Escribe o registra un cliente"
                        />
                        <small style={{ color: '#64748b', fontSize: '10.5px', marginTop: '4px', display: 'block' }}>
                            Modificable si deseas tipear directamente.
                        </small>
                    </div>

                    <div className="total-card">
                        <span className="total-label">TOTAL A PAGAR</span>
                        <span className="total-amount">S/ {total.toFixed(2)}</span>
                    </div>

                    <div className="form-group" style={{ marginBottom: '15px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#666', display: 'block', marginBottom: '5px' }}>
                            FORMATO DE IMPRESIÓN
                        </label>
                        <select 
                            className="form-control" 
                            value={tipoComprobante} 
                            onChange={(e) => setTipoComprobante(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ddd' }}
                        >
                            <option value="ticket">Ticketera (80mm)</option>
                            <option value="pdf">Hoja A4 (PDF)</option>
                        </select>
                    </div>

                    <div className="venta-actions">
                        <button className="btn-confirm" onClick={procesarVentas} disabled={carrito.length === 0}>
                            <i className="fa fa-check"></i> FINALIZAR VENTA
                        </button>
                        <button className="btn-cancel-venta" onClick={cancelarVenta} style={{ marginBottom: '10px' }}>
                            <i className="fa fa-times"></i> CANCELAR
                        </button>
                        <button type="button" className="btn-vender-sidebar" onClick={openModal}>
                            <i className="fa fa-plus"></i> Registrar Cliente
                        </button>
                    </div>
                </aside>

                {/* MODAL DE REGISTRO */}
                {isModalOpen && (
                    <div className="modal" style={{ display: 'flex' }}>
                        <div className="modal-content" style={{ maxWidth: '500px', width: '100%' }}>
                            <form onSubmit={handleSave}>
                                <h2>REGISTRAR CLIENTE</h2>
                                
                                <div className="form-group">
                                    <label>DNI del Cliente:</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <input 
                                            type="text" 
                                            maxLength={8}
                                            value={formData.dni} 
                                            required 
                                            style={{ flex: 1 }}
                                            placeholder="Ingresa 8 dígitos"
                                            onChange={(e) => setFormData({...formData, dni: e.target.value.replace(/\D/g, '')})} 
                                        />
                                        <button 
                                            type="button" 
                                            onClick={handleConsultarDni} 
                                            disabled={loadingDni}
                                            style={{ padding: '0 15px', background: '#0070f3', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                                        >
                                            {loadingDni ? 'Buscando...' : 'Buscar RENIEC'}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Nombres:</label>
                                    <input 
                                        type="text" 
                                        value={formData.nombres} 
                                        required 
                                        placeholder="Nombres completos" 
                                        onChange={(e) => setFormData({...formData, nombres: e.target.value})}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Apellido Paterno:</label>
                                        <input 
                                            type="text" 
                                            value={formData.apellido_paterno} 
                                            required 
                                            placeholder="Ap. Paterno" 
                                            onChange={(e) => setFormData({...formData, apellido_paterno: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: 1 }}>
                                        <label>Apellido Materno:</label>
                                        <input 
                                            type="text" 
                                            value={formData.apellido_materno} 
                                            placeholder="Ap. Materno" 
                                            onChange={(e) => setFormData({...formData, apellido_materno: e.target.value})}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Número de Celular (Opcional):</label>
                                    <input 
                                        type="text" 
                                        maxLength={15} 
                                        value={formData.num_cel} 
                                        placeholder="Ej: 906145083 (Opcional)"
                                        onChange={(e) => setFormData({...formData, num_cel: e.target.value})} 
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Correo Electrónico (Opcional):</label>
                                    <input 
                                        type="email" 
                                        value={formData.email} 
                                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                                        placeholder="ejemplo@correo.com (Opcional)"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Comentarios / Notas:</label>
                                    <textarea 
                                        value={formData.comments} 
                                        onChange={(e) => setFormData({...formData, comments: e.target.value})} 
                                        placeholder="Observaciones sobre el cliente... (Opcional)"
                                        style={{ width: '100%', height: '70px', borderRadius: '6px', padding: '8px', border: '1px solid #ddd', resize: 'none' }}
                                    />
                                </div>

                                <div className="actions" style={{ marginTop: '20px' }}>
                                    <button type="submit" className="btn-save" disabled={!formData.nombres}>
                                        Guardar Cliente
                                    </button>
                                    <button type="button" className="btn-cancel" onClick={closeModal}>Cancelar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>  
    );
};

export default RegistrarVentas;